import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import { FieldTypeModel } from '../../models/field-type.model';
import { PrefillValueModel } from '../../models/prefill-value.model';
import { TemplateFieldModel } from '../../models/template-field.model';

/** Lo que resuelve el modal al guardar. */
export interface IPrefillValueModalResult {
  templateFieldId: string;
  value: string;
}

/**
 * Alta y edicion de un valor de prerellenado.
 *
 * El API NO impide dos valores para el mismo campo (no hay UNIQUE ni
 * validacion), asi que el duplicado se evita AQUI: la pagina pasa solo los
 * campos de la plantilla que aun no tienen valor. Al editar, el campo ya no se
 * puede cambiar: el PUT solo acepta `value`.
 */
@Component({
  selector: 'app-prefill-value-modal',
  imports: [NgSelectComponent, ReactiveFormsModule],
  templateUrl: './prefill-value-modal.component.html',
  styleUrl: './prefill-value-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrefillValueModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly formBuilder = inject(FormBuilder);

  /** `null` = alta. Con modelo = edicion. */
  protected readonly prefillValue = signal<PrefillValueModel | null>(null);

  /** Campos de la plantilla que todavia NO tienen valor. */
  protected readonly availableFields = signal<TemplateFieldModel[]>([]);

  /** Catalogo de tipos, para saber si el valor es protegido o multilinea. */
  protected readonly fieldTypes = signal<FieldTypeModel[]>([]);

  protected readonly isEdit = computed(() => this.prefillValue() !== null);

  protected readonly title = computed(() => (this.isEdit() ? 'Editar valor' : 'Nuevo valor'));

  protected readonly form = this.formBuilder.nonNullable.group({
    templateFieldId: ['', [Validators.required]],
    value: ['', [Validators.required, Validators.maxLength(2000)]],
  });

  /**
   * Lo que consume el `<ng-select>` de campo. Se precalcula la etiqueta en vez
   * de componerla en la plantilla porque `bindLabel` apunta a UNA propiedad y
   * esa misma propiedad es sobre la que ng-select busca.
   */
  protected readonly fieldOptions = computed(() =>
    this.availableFields().map((item) => ({
      templateFieldId: item.templateFieldId,
      label: `${item.label} (${item.fieldType})`,
    })),
  );

  private readonly selectedId = toSignal(this.form.controls.templateFieldId.valueChanges, {
    initialValue: '',
  });

  /** Campo de plantilla elegido. */
  protected readonly selectedField = computed(() => {
    const id = this.selectedId() || this.prefillValue()?.templateFieldId || '';
    return this.availableFields().find((item) => item.templateFieldId === id) ?? null;
  });

  /** Tipo del campo elegido. */
  protected readonly selectedType = computed(() => {
    const field = this.selectedField();
    if (!field) return null;
    return this.fieldTypes().find((item) => item.fieldType === field.fieldType) ?? null;
  });

  protected readonly isProtected = computed(() => this.selectedType()?.protected === true);
  protected readonly isMultiline = computed(() => this.selectedType()?.multiline === true);

  /** Al capturar un valor protegido se escribe oculto; se puede revelar. */
  protected readonly revealed = signal(false);

  protected readonly inputType = computed(() =>
    this.isProtected() && !this.revealed() ? 'password' : 'text',
  );

  protected readonly submitted = signal(false);

  /**
   * Carga el modal.
   *
   * @param availableFields al editar debe incluir el campo del propio valor,
   * para poder mostrarlo.
   */
  init(
    prefillValue: PrefillValueModel | null,
    availableFields: TemplateFieldModel[],
    fieldTypes: FieldTypeModel[],
  ): void {
    this.availableFields.set(availableFields);
    this.fieldTypes.set(fieldTypes);
    this.prefillValue.set(prefillValue);

    if (!prefillValue) return;

    this.form.patchValue({
      templateFieldId: prefillValue.templateFieldId,
      value: prefillValue.value,
    });

    // El PUT del API solo acepta `value`.
    this.form.controls.templateFieldId.disable();
  }

  protected invalid(control: 'templateFieldId' | 'value'): boolean {
    const field = this.form.controls[control];
    return field.invalid && (field.touched || this.submitted());
  }

  /**
   * Atributos que ng-select copia al `<input role="combobox">` interno, que es
   * el elemento que recibe el foco y, por tanto, el unico al que el lector de
   * pantalla le lee `aria-describedby` y `aria-invalid`. El `id` NO va aqui:
   * lo pone `[labelForId]`, que es lo que enlaza el `<label for>`.
   */
  protected fieldInputAttrs(): Record<string, string> {
    return {
      'aria-describedby': this.invalid('templateFieldId')
        ? 'ks-prefill-value-field-error'
        : 'ks-prefill-value-field-hint',
      'aria-invalid': this.invalid('templateFieldId') ? 'true' : 'false',
    };
  }

  protected toggleReveal(): void {
    this.revealed.update((value) => !value);
  }

  protected save(): void {
    this.submitted.set(true);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const current = this.prefillValue();

    const result: IPrefillValueModalResult = {
      templateFieldId: current ? current.templateFieldId : value.templateFieldId,
      value: value.value,
    };

    this.activeModal.close(result);
  }

  protected cancel(): void {
    this.activeModal.dismiss('cancel');
  }
}
